import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ROICalculator = () => {
  const [formData, setFormData] = useState({
    organizationType: '',
    employeeCount: '',
    currentTrainingBudget: '',
    implementationTimeline: '',
    primaryGoal: ''
  });

  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const organizationTypes = [
    { value: 'university', label: 'University/College' },
    { value: 'corporate', label: 'Corporate Enterprise' },
    { value: 'government', label: 'Government Agency' },
    { value: 'nonprofit', label: 'Non-Profit Organization' }
  ];

  const employeeCounts = [
    { value: '50-200', label: '50-200 employees' },
    { value: '200-500', label: '200-500 employees' },
    { value: '500-1000', label: '500-1,000 employees' },
    { value: '1000-5000', label: '1,000-5,000 employees' },
    { value: '5000+', label: '5,000+ employees' }
  ];

  const timelines = [
    { value: '3months', label: '3 months' },
    { value: '6months', label: '6 months' },
    { value: '12months', label: '12 months' },
    { value: '18months', label: '18+ months' }
  ];

  const goals = [
    { value: 'skill-development', label: 'Skill Development' },
    { value: 'cost-reduction', label: 'Cost Reduction' },
    { value: 'productivity', label: 'Productivity Increase' },
    { value: 'innovation', label: 'Innovation & Competitiveness' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateROI = () => {
    setIsCalculating(true);
    
    // Simulate calculation delay
    setTimeout(() => {
      const baseROI = {
        university: { savings: 250000, productivity: 35, timeline: 8 },
        corporate: { savings: 500000, productivity: 45, timeline: 6 },
        government: { savings: 350000, productivity: 30, timeline: 10 },
        nonprofit: { savings: 150000, productivity: 25, timeline: 12 }
      };

      const multipliers = {
        '50-200': 0.7,
        '200-500': 1.0,
        '500-1000': 1.3,
        '1000-5000': 1.8,
        '5000+': 2.5
      };

      const base = baseROI[formData.organizationType] || baseROI.corporate;
      const multiplier = multipliers[formData.employeeCount] || 1.0;

      setResults({
        annualSavings: Math.round(base.savings * multiplier),
        productivityIncrease: base.productivity,
        paybackPeriod: base.timeline,
        threeYearROI: Math.round((base.savings * multiplier * 3) / 100000) * 100,
        trainingCostReduction: 40,
        implementationCost: Math.round(base.savings * multiplier * 0.3)
      });

      setIsCalculating(false);
    }, 2000);
  };

  const isFormValid = Object.values(formData).every(value => value !== '');

  return (
    <section className="py-20 bg-gradient-to-br from-primary to-secondary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-foreground mb-4">
            Calculate Your
            <span className="gradient-text"> ROI</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Discover the potential cost savings and productivity gains from implementing our AI/Robotics training solutions
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column - Calculator Form */}
          <div className="holographic-card p-8 bg-card/50 backdrop-blur-md">
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-6">
                <Icon name="Calculator" size={24} className="text-accent" />
                <h3 className="text-xl font-bold text-foreground">ROI Calculator</h3>
              </div>

              <Select
                label="Organization Type"
                options={organizationTypes}
                value={formData.organizationType}
                onChange={(value) => handleInputChange('organizationType', value)}
                placeholder="Select your organization type"
                required
              />

              <Select
                label="Number of Employees"
                options={employeeCounts}
                value={formData.employeeCount}
                onChange={(value) => handleInputChange('employeeCount', value)}
                placeholder="Select employee count"
                required
              />

              <Input
                label="Current Annual Training Budget"
                type="text"
                placeholder="e.g., $100,000"
                value={formData.currentTrainingBudget}
                onChange={(e) => handleInputChange('currentTrainingBudget', e.target.value)}
                required
              />

              <Select
                label="Implementation Timeline"
                options={timelines}
                value={formData.implementationTimeline}
                onChange={(value) => handleInputChange('implementationTimeline', value)}
                placeholder="Select timeline"
                required
              />

              <Select
                label="Primary Goal"
                options={goals}
                value={formData.primaryGoal}
                onChange={(value) => handleInputChange('primaryGoal', value)}
                placeholder="Select primary goal"
                required
              />

              <Button
                variant="default"
                size="lg"
                fullWidth
                className="morphing-cta text-white font-semibold"
                onClick={calculateROI}
                disabled={!isFormValid}
                loading={isCalculating}
                iconName="TrendingUp"
                iconPosition="left"
              >
                {isCalculating ? 'Calculating...' : 'Calculate ROI'}
              </Button>
            </div>
          </div>

          {/* Right Column - Results */}
          <div className="space-y-6">
            {!results ? (
              <div className="holographic-card p-8 bg-card/30 backdrop-blur-md">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-accent/20 to-cyber-purple/20 rounded-full flex items-center justify-center mx-auto">
                    <Icon name="BarChart3" size={32} className="text-accent" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">Your ROI Results</h3>
                  <p className="text-muted-foreground">
                    Fill out the form to see your personalized ROI calculation and potential savings
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="holographic-card p-6 bg-card/50 backdrop-blur-md text-center">
                    <div className="text-3xl font-bold text-success mb-2">
                      ${results.annualSavings.toLocaleString()}
                    </div>
                    <div className="text-sm text-muted-foreground">Annual Savings</div>
                  </div>
                  
                  <div className="holographic-card p-6 bg-card/50 backdrop-blur-md text-center">
                    <div className="text-3xl font-bold text-accent mb-2">
                      {results.productivityIncrease}%
                    </div>
                    <div className="text-sm text-muted-foreground">Productivity Increase</div>
                  </div>
                  
                  <div className="holographic-card p-6 bg-card/50 backdrop-blur-md text-center">
                    <div className="text-3xl font-bold text-warning mb-2">
                      {results.paybackPeriod} months
                    </div>
                    <div className="text-sm text-muted-foreground">Payback Period</div>
                  </div>
                  
                  <div className="holographic-card p-6 bg-card/50 backdrop-blur-md text-center">
                    <div className="text-3xl font-bold text-cyber-purple mb-2">
                      {results.threeYearROI}%
                    </div>
                    <div className="text-sm text-muted-foreground">3-Year ROI</div>
                  </div>
                </div>

                {/* Detailed Breakdown */}
                <div className="holographic-card p-6 bg-card/50 backdrop-blur-md">
                  <h4 className="text-lg font-semibold text-foreground mb-4">Detailed Breakdown</h4>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-border">
                      <span className="text-muted-foreground">Implementation Cost</span>
                      <span className="font-semibold text-foreground">${results.implementationCost.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border">
                      <span className="text-muted-foreground">Training Cost Reduction</span>
                      <span className="font-semibold text-success">{results.trainingCostReduction}%</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border">
                      <span className="text-muted-foreground">Annual Savings</span>
                      <span className="font-semibold text-success">${results.annualSavings.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-muted-foreground">Net 3-Year Benefit</span>
                      <span className="font-semibold text-success">${(results.annualSavings * 3 - results.implementationCost).toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="space-y-3">
                  <Button
                    variant="default"
                    size="lg"
                    fullWidth
                    className="morphing-cta text-white font-semibold"
                    iconName="Calendar"
                    iconPosition="left"
                  >
                    Schedule Strategy Call
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="lg"
                    fullWidth
                    iconName="Download"
                    iconPosition="left"
                  >
                    Download Full Report
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;